import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './index.module.scss';
import HeadContainer from '../../head-container';
import { getImageFromS3 } from '../../../imageService';

const GAME_CONFIG = {
  SPAWN_RATE_MIN: 0.008, // мінімальна частота появи листочків
  SPAWN_RATE_MAX: 0.02, // максимальна частота появи листочків
  SPAWN_RATE_INCREASE: 0.02, // швидкість збільшення частоти
  GOOD_LEAF_CHANCE: 0.7, // ймовірність хорошого листочка
  MAX_LEAVES: 25 // максимальна кількість листочків на екрані
};

const LEAF_IMAGES = {
  good: [
    'images/selected-harvest-challenge/good-leaf-1.png',
    'images/selected-harvest-challenge/good-leaf-2.png',
    'images/selected-harvest-challenge/good-leaf-3.png',
    'images/selected-harvest-challenge/good-leaf-4.png',
    'images/selected-harvest-challenge/good-leaf-5.png',
    'images/selected-harvest-challenge/good-leaf-6.png',
    'images/selected-harvest-challenge/good-leaf-7.png',
    'images/selected-harvest-challenge/good-leaf-8.png',
    'images/selected-harvest-challenge/good-leaf-9.png',
    'images/selected-harvest-challenge/good-leaf-10.png',
    'images/selected-harvest-challenge/good-leaf-11.png',
    'images/selected-harvest-challenge/good-leaf-12.png',
    'images/selected-harvest-challenge/good-leaf-13.png'
  ],
  bad: [
    'images/selected-harvest-challenge/bad-leaf-1.png',
    'images/selected-harvest-challenge/bad-leaf-2.png'
  ]
};

const filledLeafResult = getImageFromS3(
  'images/selected-harvest-challenge/filledLeafResult.png'
);
const emptyLeafResult = getImageFromS3(
  'images/selected-harvest-challenge/emptyLeafResult.png'
);

const LeafsGame = ({ backgroundImage }) => {
  const [isWelcomeScreen, setIsWelcomeScreen] = useState(true);
  const [isInfoScreen, setIsInfoScreen] = useState(false);
  const [isGameScreen, setIsGameScreen] = useState(false);

  const [truckPosition, setTruckPosition] = useState(50); // позиція у відсотках
  const [leaves, setLeaves] = useState([]);
  const [score, setScore] = useState(0);
  const [totalGoodLeaves, setTotalGoodLeaves] = useState(0); // загальна кількість хороших листочків
  const [timeLeft, setTimeLeft] = useState(60); // 60 секунд
  const [gameSpeed, setGameSpeed] = useState(0.35); // швидкість падіння листя
  const [isGameActive, setIsGameActive] = useState(false);
  const [badLeafAnimation, setBadLeafAnimation] = useState(null);
  const [animationFrame, setAnimationFrame] = useState(0);
  const [showAnimation, setShowAnimation] = useState(false);
  const [goodLeafAnimation, setGoodLeafAnimation] = useState(null);
  const [goodAnimationFrame, setGoodAnimationFrame] = useState(0);
  const [showGoodAnimation, setShowGoodAnimation] = useState(false);
  const [leafFillAnimation, setLeafFillAnimation] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showAfterResult, setShowAfterResult] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalTotalGoodLeaves, setFinalTotalGoodLeaves] = useState(0);

  const gameRef = useRef();
  const animationRef = useRef();
  const leafIdRef = useRef(0);
  const lastUpdateRef = useRef(0);
  const truckPositionRef = useRef(50);
  const gameSpeedRef = useRef(gameSpeed);
  const lastGameUpdateTimeRef = useRef(0);
  const scoreRef = useRef(0);
  const prevLeafFillRef = useRef(0);

  // Функція для перевірки, чи не займає позиція місце іншого листочка
  const isPositionOccupied = useCallback((newX, existingLeaves) => {
    const minDistance = 8; // мінімальна відстань між листочками (в відсотках)

    return existingLeaves.some((leaf) => {
      if (leaf.y > 20) return false;

      const distance = Math.abs(leaf.x - newX);
      return distance < minDistance;
    });
  }, []);

  // Функція для створення нового листочка з картинкою (однаковий розмір)
  const createLeaf = useCallback(
    (currentSpeed, existingLeaves) => {
      const isGood = Math.random() < GAME_CONFIG.GOOD_LEAF_CHANCE;

      // Обираємо рандомну картинку залежно від типу листочка
      const imageArray = isGood ? LEAF_IMAGES.good : LEAF_IMAGES.bad;
      const randomImageSrc =
        imageArray[Math.floor(Math.random() * imageArray.length)];

      // Генеруємо позицію з перевіркою на перетин з існуючими листочками
      let xPosition;
      let attempts = 0;
      const maxAttempts = 10; // максимум спроб знайти вільне місце

      do {
        xPosition = 10 + Math.random() * 80;
        attempts++;
      } while (
        isPositionOccupied(xPosition, existingLeaves) &&
        attempts < maxAttempts
      );

      // Якщо не знайшли вільне місце за 10 спроб, повертаємо null
      if (
        attempts >= maxAttempts &&
        isPositionOccupied(xPosition, existingLeaves)
      ) {
        return null;
      }

      return {
        id: leafIdRef.current++,
        x: xPosition,
        y: -20, // починаємо вище екрану
        isGood,
        speed: currentSpeed, // фіксована швидкість на момент створення
        image: randomImageSrc,
        rotation: Math.random() * 360 // рандомний кут повороту від 0 до 360 градусів
      };
    },
    [isPositionOccupied]
  );

  // Функція для оновлення позиції машинки з швидким відгуком
  const updateTruckPosition = useCallback(
    (newPosition) => {
      const constrainedPosition = Math.max(8, Math.min(92, newPosition));

      truckPositionRef.current = constrainedPosition;

      const now = Date.now();
      if (now - lastUpdateRef.current > 16) {
        setTruckPosition(constrainedPosition);
        lastUpdateRef.current = now;
      }
    },
    [truckPosition]
  );

  // Керування машинкою
  const handleTouchStart = useCallback(
    (e) => {
      if (!isGameActive) return;
      const touch = e.touches[0];
      const rect = gameRef.current?.getBoundingClientRect();
      if (!rect) return;
      const newPosition = ((touch.clientX - rect.left) / rect.width) * 100;
      requestAnimationFrame(() => updateTruckPosition(newPosition));
    },
    [isGameActive, updateTruckPosition]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isGameActive) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rect = gameRef.current?.getBoundingClientRect();
      if (!rect) return;
      const newPosition = ((touch.clientX - rect.left) / rect.width) * 100;
      requestAnimationFrame(() => updateTruckPosition(newPosition));
    },
    [isGameActive, updateTruckPosition]
  );

  // Підтримка мишки
  const handleMouseMove = useCallback(
    (e) => {
      if (!isGameActive) return;
      const rect = gameRef.current?.getBoundingClientRect();
      if (!rect) return;
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      requestAnimationFrame(() => updateTruckPosition(newPosition));
    },
    [isGameActive, updateTruckPosition]
  );

  // Перевірка зіткнення по справжніх розмірах машинки і листочків
  const checkCollision = useCallback((leaf) => {
    if (!gameRef.current) return false;

    // Отримуємо розміри контейнера
    const containerWidth = gameRef.current.offsetWidth;
    const containerHeight = gameRef.current.offsetHeight;

    // Конвертуємо відсоткові координати в пікселі
    const truckPixelX = (truckPositionRef.current / 100) * containerWidth;
    const leafPixelX = (leaf.x / 100) * containerWidth;
    const leafPixelY = (leaf.y / 100) * containerHeight;

    // СПРАВЖНІ розміри + похибка 10px
    const truckWidthPx = 150; // фіксована ширина машинки 150px
    const leafWidthPx = 40; // реальна ширина листочка
    const errorMargin = 10; // похибка в 10px

    // Координати машинки з похибкою (враховуємо реальну висоту картинки)
    const truckLeft = truckPixelX - truckWidthPx / 2 - errorMargin;
    const truckRight = truckPixelX + truckWidthPx / 2 + errorMargin;

    // Машинка в CSS: bottom: 100px - скоригуємо зону під реальну висоту
    const truckBottom = containerHeight - 100 + errorMargin; // низ машинки
    const truckTop = containerHeight - 170 - errorMargin; // верх машинки (~70px висота)

    // Координати листочка з похибкою (центр на поточній позиції)
    const leafLeft = leafPixelX - leafWidthPx / 2 - errorMargin;
    const leafRight = leafPixelX + leafWidthPx / 2 + errorMargin;
    const leafTop = leafPixelY - 15 - errorMargin; // половина висоти листочка
    const leafBottom = leafPixelY + 15 + errorMargin; // половина висоти листочка

    // Перевірка точного перекриття прямокутників
    const horizontalOverlap = leafRight >= truckLeft && leafLeft <= truckRight;
    const verticalOverlap = leafBottom >= truckTop && leafTop <= truckBottom;
    const hasCollision = horizontalOverlap && verticalOverlap;

    return hasCollision;
  }, []);

  // Вібрація при поганому листочку
  const triggerVibration = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
  }, []);

  const updateGame = useCallback(() => {
    if (!isGameActive) return;

    // Максимальна частота оновлення для надійного відстеження зіткнень
    const now = Date.now();
    if (now - lastGameUpdateTimeRef.current < 4) {
      return;
    }
    lastGameUpdateTimeRef.current = now;

    setLeaves((prevLeaves) => {
      // Обмежуємо кількість листочків на екрані
      if (prevLeaves.length > GAME_CONFIG.MAX_LEAVES) {
        return prevLeaves.slice(0, GAME_CONFIG.MAX_LEAVES);
      }

      const updatedLeaves = prevLeaves.map((leaf) => ({
        ...leaf,
        y: leaf.y + leaf.speed
      }));

      const remainingLeaves = [];
      let scoreIncrement = 0; // лічильник для збільшення score

      updatedLeaves.forEach((leaf) => {
        // Подвійна перевірка зіткнення для надійності
        const collisionBefore = checkCollision({
          ...leaf,
          y: leaf.y - leaf.speed
        });
        const collisionAfter = checkCollision(leaf);
        const collision = collisionBefore || collisionAfter;

        if (collision) {
          // Обробляємо зіткнення
          if (leaf.isGood) {
            scoreIncrement++; // збільшуємо лічильник
            // Анімація хорошого листочка
            setGoodLeafAnimation({ x: leaf.x, y: leaf.y });
            setShowGoodAnimation(true);
            setGoodAnimationFrame(0);
          } else {
            // Анімація поганого листочка
            setBadLeafAnimation({ x: leaf.x, y: leaf.y });
            setShowAnimation(true);
            setAnimationFrame(0);
            triggerVibration();
          }
          // Листочок НЕ додається до remainingLeaves - він зникає
        } else if (leaf.y < 105) {
          // Листочок залишається тільки якщо немає зіткнення і не впав
          remainingLeaves.push(leaf);
        }
        // Листочки що впали (y >= 105) теж не додаються
      });

      // Оновлюємо score одним разом після обробки всіх листочків
      if (scoreIncrement > 0) {
        scoreRef.current += scoreIncrement;
        setScore(scoreRef.current);
      }

      // Додаємо нові листочки випадково (з поступовим збільшенням частоти)
      const currentGameSpeed = gameSpeedRef.current;
      const spawnRate = Math.min(
        GAME_CONFIG.SPAWN_RATE_MAX,
        GAME_CONFIG.SPAWN_RATE_MIN +
          currentGameSpeed * GAME_CONFIG.SPAWN_RATE_INCREASE
      );
      if (
        Math.random() < spawnRate &&
        remainingLeaves.length < GAME_CONFIG.MAX_LEAVES
      ) {
        const newLeaf = createLeaf(currentGameSpeed, remainingLeaves);
        // Додаємо листочок тільки якщо він був успішно створений
        if (newLeaf) {
          if (newLeaf.isGood) {
            setTotalGoodLeaves((prev) => prev + 1);
          }
          remainingLeaves.push(newLeaf);
        }
      }

      return remainingLeaves;
    });
  }, [isGameActive, checkCollision, createLeaf, triggerVibration]);

  // Ігровий цикл
  useEffect(() => {
    if (isGameActive) {
      const gameLoop = (timestamp) => {
        updateGame();
        animationRef.current = requestAnimationFrame(gameLoop);
      };
      animationRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isGameActive, updateGame]);

  // Таймер
  useEffect(() => {
    if (isGameActive && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setIsGameActive(false);
      setFinalScore(score);
      setFinalTotalGoodLeaves(totalGoodLeaves);
      setShowResult(true);
      setIsGameScreen(false);
    }
  }, [isGameActive, timeLeft]);

  // Оновлення ref при зміні gameSpeed
  useEffect(() => {
    gameSpeedRef.current = gameSpeed;
  }, [gameSpeed]);

  // Збільшення швидкості з часом
  useEffect(() => {
    if (isGameActive) {
      const speedIncrease = setTimeout(() => {
        setGameSpeed((prev) => Math.min(prev + 0.15, 3.5));
      }, 15000); // кожні 15 секунд
      return () => clearTimeout(speedIncrease);
    }
  }, [isGameActive, gameSpeed]);

  // Анімація з 3 PNG кадрами (погані листочки)
  useEffect(() => {
    if (showAnimation) {
      const timer = setTimeout(() => {
        if (animationFrame < 2) {
          setAnimationFrame((prev) => prev + 1);
        } else {
          // Після показу всіх 3 кадрів - приховуємо анімацію
          setShowAnimation(false);
          setBadLeafAnimation(null);
          setAnimationFrame(0);
        }
      }, 100); // 100ms між кадрами (швидка анімація)

      return () => clearTimeout(timer);
    }
  }, [showAnimation, animationFrame]);

  // Анімація з 3 PNG кадрами (хороші листочки)
  useEffect(() => {
    if (showGoodAnimation) {
      const timer = setTimeout(() => {
        if (goodAnimationFrame < 2) {
          setGoodAnimationFrame((prev) => prev + 1);
        } else {
          // Після показу всіх 3 кадрів - приховуємо анімацію
          setShowGoodAnimation(false);
          setGoodLeafAnimation(null);
          setGoodAnimationFrame(0);
        }
      }, 100); // 100ms між кадрами (швидка анімація)

      return () => clearTimeout(timer);
    }
  }, [showGoodAnimation, goodAnimationFrame]);

  // Анімація заповнення листочків на екрані результатів
  useEffect(() => {
    if (showResult) {
      // Скидаємо анімацію
      setLeafFillAnimation(0);

      // Розраховуємо кількість листочків які треба заповнити
      const percent =
        finalTotalGoodLeaves > 0
          ? Math.round((finalScore / finalTotalGoodLeaves) * 100)
          : 0;
      const targetFilledCount = Math.round((percent / 100) * 5);

      if (targetFilledCount === 0) {
        // Якщо немає листочків для заповнення, одразу переходимо далі через 3 секунди
        const autoNext = setTimeout(() => {
          setIsGameScreen(false);
          setIsInfoScreen(false);
          setIsWelcomeScreen(false);
          setShowResult(false);
          setShowAfterResult(true);
        }, 3000);

        return () => clearTimeout(autoNext);
      } else {
        // Заповнюємо листочки по одному
        const interval = 3000 / targetFilledCount; // розподіляємо 3 секунди на всі листочки

        const fillTimer = setInterval(() => {
          setLeafFillAnimation((prev) => {
            if (prev < targetFilledCount) {
              return prev + 1;
            } else {
              clearInterval(fillTimer);
              // Після завершення анімації чекаємо ще трохи і переходимо далі
              setTimeout(() => {
                setIsGameScreen(false);
                setIsInfoScreen(false);
                setIsWelcomeScreen(false);
                setShowResult(false);
                setShowAfterResult(true);
              }, 1000);
              return prev;
            }
          });
        }, interval);

        return () => clearInterval(fillTimer);
      }
    }
  }, [showResult, finalScore, finalTotalGoodLeaves]);

  // Оновлення ref для відстеження попереднього значення заповнення листочків
  useEffect(() => {
    prevLeafFillRef.current = leafFillAnimation;
  }, [leafFillAnimation]);

  const startGame = () => {
    setIsGameActive(true);
    setScore(0);
    scoreRef.current = 0;
    setTotalGoodLeaves(0);
    setFinalScore(0);
    setFinalTotalGoodLeaves(0);
    setTimeLeft(60);
    setGameSpeed(0.35);
    setLeaves([]);
    setTruckPosition(50);
    truckPositionRef.current = 50;
    setShowResult(false);
    setLeafFillAnimation(0);
    prevLeafFillRef.current = 0;
  };

  return (
    <div className={styles.wrapper} style={{ backgroundImage }}>
      {isWelcomeScreen && (
        <div className={styles.welcomeContainer}>
          <img
            src={getImageFromS3(
              'images/selected-harvest-challenge/welcome-image.png'
            )}
            alt="welcome-challenge"
            className={styles.welcomeImage}
          />
          <div style={{ padding: '0 30px' }}>
            <p className={styles.description}>
              Лише найкраще листя стає частиною справжньої майстерності.
            </p>
            <button
              onClick={() => {
                setIsWelcomeScreen(false);
                setIsInfoScreen(true);
              }}
            >
              Збирати!
            </button>
          </div>
        </div>
      )}
      {isInfoScreen && !isWelcomeScreen && (
        <div className={styles.infoContainer}>
          <img
            src={getImageFromS3(
              'images/selected-harvest-challenge/info-game.png'
            )}
            alt="info-image"
          />
          <button
            onClick={() => {
              setIsGameScreen(true);
              setIsInfoScreen(false);
              startGame();
            }}
          >
            Почати!
          </button>
        </div>
      )}
      {!isInfoScreen && !isWelcomeScreen && isGameScreen && (
        <div className={styles.gameContainer}>
          <div className={styles.gameHeader}>
            <div className={styles.timerContainer}>
              <span>
                {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:
                {String(timeLeft % 60).padStart(2, '0')}
              </span>
            </div>
            <div className={styles.scoreContainer}>
              <span>{String(score).padStart(2, '0')}</span>
            </div>
          </div>
          <div
            className={styles.gameArea}
            ref={gameRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onMouseMove={handleMouseMove}
            style={{ touchAction: 'none' }}
          >
            {leaves.map((leaf) => (
              <img
                key={leaf.id}
                id={leaf.id}
                src={getImageFromS3(leaf.image)}
                alt={leaf.isGood ? 'good-leaf' : 'bad-leaf'}
                style={{
                  left: `${leaf.x}%`,
                  top: `${leaf.y}%`,
                  width: '40px',
                  height: 'auto',
                  position: 'absolute',
                  background: 'transparent'
                }}
                loading="lazy"
                draggable={false}
              />
            ))}
            {badLeafAnimation && showAnimation && (
              <div
                className={styles.badLeafExplosion}
                style={{
                  left: `${badLeafAnimation.x}%`,
                  top: `${badLeafAnimation.y}%`
                }}
              >
                <img
                  src={getImageFromS3(
                    `images/selected-harvest-challenge/bad-animation-frame-${
                      animationFrame + 1
                    }.png`
                  )}
                  alt={`animation-frame-${animationFrame + 1}`}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            {goodLeafAnimation && showGoodAnimation && (
              <div
                className={styles.goodLeafExplosion}
                style={{
                  left: `${goodLeafAnimation.x}%`,
                  top: `${goodLeafAnimation.y}%`
                }}
              >
                <img
                  src={getImageFromS3(
                    `images/selected-harvest-challenge/good-animation-frame-${
                      goodAnimationFrame + 1
                    }.png`
                  )}
                  alt={`good-animation-frame-${goodAnimationFrame + 1}`}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            <img
              src={getImageFromS3(
                'images/selected-harvest-challenge/truck.png'
              )}
              alt="truck"
              className={styles.truck}
              style={{ left: `${truckPosition}%` }}
            />
          </div>
        </div>
      )}
      {!isGameScreen && !isWelcomeScreen && !isInfoScreen && showResult && (
        <div className={styles.resultContainer}>
          <div className={styles.resultInfoBlock}>
            <p>
              Твій <br></br>Результат
            </p>
            <div className={styles.leafRatingContainer}>
              {[...Array(5)].map((item, index) => {
                const isFilled = index < leafFillAnimation;
                const isJustFilled =
                  index === leafFillAnimation - 1 &&
                  leafFillAnimation > prevLeafFillRef.current;

                return (
                  <img
                    key={index}
                    src={isFilled ? filledLeafResult : emptyLeafResult}
                    alt={`leaf-${index?.toString()}`}
                    className={`${styles.leafIcon} ${
                      isJustFilled ? styles.filling : ''
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showAfterResult &&
        !showResult &&
        !isWelcomeScreen &&
        !isInfoScreen &&
        !isGameScreen && (
          <div className={styles.afterResultContainer}>
            <div className={styles.afterResultContent}>
              <img
                src={getImageFromS3(
                  `images/selected-harvest-challenge/${
                    (finalTotalGoodLeaves > 0
                      ? Math.round((finalScore / finalTotalGoodLeaves) * 100)
                      : 0) <= 33
                      ? 'bronze-result.png'
                      : (finalTotalGoodLeaves > 0
                          ? Math.round(
                              (finalScore / finalTotalGoodLeaves) * 100
                            )
                          : 0) >= 66
                      ? 'gold-result.png'
                      : 'silver-result.png'
                  }`
                )}
                alt="after-result-image"
              />
              <div className={styles.description}>
                {(finalTotalGoodLeaves > 0
                  ? Math.round((finalScore / finalTotalGoodLeaves) * 100)
                  : 0) <= 33
                  ? 'Трохи більше практики – і результат буде ще кращим!'
                  : (finalTotalGoodLeaves > 0
                      ? Math.round((finalScore / finalTotalGoodLeaves) * 100)
                      : 0) >= 66
                  ? 'Твій погляд безпомилково відрізняє якість.\n' +
                    'Продовжуй вдосконалювати свої навички експерта.'
                  : 'Цей тест був непростим, але головне – це прагнення до знань. Трохи більше практики – і результат буде ще кращим!'}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default LeafsGame;
